import { useState } from "react";
import { Eye, EyeOff, Lock, Mail, Check, X } from "lucide-react";
import toast from "react-hot-toast";
import { changePassword } from "../../Redux/Actions/passwordChangeAction";
import { useDispatch } from "react-redux";
import './ChangePassword.css';

const ChangePasswordPopup = ({ onClose }) => {
  const [email, setEmailId] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validatePassword = (pwd) => {
    return pwd.length >= 8;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !newPassword || !confirmPassword) {
      toast.error("All fields are required.");
      return;
    }

    if (!validateEmail(email)) {
      toast.error("Please enter a valid email.");
      return;
    }

    if (!validatePassword(newPassword)) {
      toast.error("Password must be at least 8 characters.");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      const result = await dispatch(changePassword({ email, pwd: newPassword }));

      if (result.success) {
        toast.success("Password updated successfully.");
        setEmailId("");
        setNewPassword("");
        setConfirmPassword("");
        onClose();
      } else {
        toast.error("Failed to update password.");
      }
    } catch (error) {
      toast.error("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const passwordsMatch = newPassword && confirmPassword && newPassword === confirmPassword;
  const passwordValid = validatePassword(newPassword);

  return (
    <div className="change-password-modal">
      <div className="change-password-container">
        {/* Header */}
        <div className="change-password-header">
          <div className="change-password-icon">
            <Lock />
          </div>
          <h2 className="change-password-title">Change Password</h2>
          <p className="change-password-subtitle">Update your password securely</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="change-password-form">
          {/* Email Field */}
          <div className="form-groups" style={{marginLeft: "25px"}}>
            <label>Email Address</label>
            <div className="input-wrapper">
              <Mail />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmailId(e.target.value)}
                placeholder="Enter your email"
                autoComplete="email"
                required
              />
            </div>
          </div>

          {/* New Password Field */}
          <div className="form-groups" style={{marginLeft: "25px"}}>
            <label>New Password</label>
            <div className="input-wrapper">
              <Lock />
              <input
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                autoComplete="new-password"
                required
              />
              <button
                type="button"
                className="toggle-password-btn"
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                {showNewPassword ? <EyeOff /> : <Eye />}
              </button>
            </div>
            {newPassword && (
              <div className={`validation-message ${passwordValid ? 'success' : 'warning'}`}>
                {passwordValid ? (
                  <>
                    <Check size={14} /> Strong password
                  </>
                ) : (
                  <>
                    ⚠ Minimum 8 characters
                  </>
                )}
              </div>
            )}
          </div>

          {/* Confirm Password Field */}
          <div className="form-groups" style={{marginLeft: "25px"}}>
            <label>Retype Password</label>
            <div className="input-wrapper">
              <Lock />
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Retype new password"
                autoComplete="new-password"
                required
              />
              <button
                type="button"
                className="toggle-password-btn"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <EyeOff /> : <Eye />}
              </button>
            </div>
            {confirmPassword && (
              <div className={`validation-message ${passwordsMatch ? 'success' : 'error'}`}>
                {passwordsMatch ? (
                  <>
                    <Check size={14} /> Passwords match
                  </>
                ) : (
                  <>
                    <X size={14} /> Passwords don't match
                  </>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="change-password-actions">
            <button
              type="submit"
              className="submit-btn primary"
              disabled={loading}
            >
              {loading ? (
                <span className="loading-spinner">
                  <span className="spinner"></span>
                  Updating...
                </span>
              ) : (
                'Change Password'
              )}
            </button>
            <button
              type="button"
              className="submit-btn secondary"
              onClick={onClose}
            >
              Cancel
            </button>
          </div>
        </form>

        {/* Footer */}
        <p className="change-password-footer">
          Password must be at least 8 characters long
        </p>
      </div>
    </div>
  );
};

export default ChangePasswordPopup;